import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge } from "../components/Shared";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { TrendUp, Users, UserCheck, Compass, Lightning } from "@phosphor-icons/react";
import { usePipeline } from "../lib/pipeline";
import FounderDashboard from "./FounderDashboard";
import AmbassadorDashboard from "./AmbassadorDashboard";

export default function Dashboard() {
  const { activePipelineSlug } = usePipeline();
  // Pipeline-aware routing: si el operador eligió un pipeline específico, mostrar
  // el dashboard especializado. "Todos" muestra el agregado clásico.
  if (activePipelineSlug === "founder") return <FounderDashboard />;
  if (activePipelineSlug === "ambassador") return <AmbassadorDashboard />;
  return <AllPipelinesDashboard />;
}

function AllPipelinesDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 text-carbon-500">Cargando...</div>;
  if (!stats) return <div className="p-10 text-signal">No se pudo cargar el dashboard.</div>;

  const t = stats.totals;

  const kpis = [
    { label: "Total Usuarios", value: t.users, tone: "carbon", icon: Users, sub: `${t.new_users} nuevos · ${t.active_users} activos` },
    { label: "Migrant-side", value: t.migrant_side ?? 0, tone: "terracotta", icon: Users, sub: `Viven en USA` },
    { label: "Origin-side", value: t.origin_side ?? 0, tone: "forest", icon: Users, sub: `En país de origen` },
    { label: "Fundadores", value: t.founders, tone: "terracotta", icon: TrendUp, sub: `Pipeline migrant-side` },
    { label: "Embajadores", value: t.ambassadors, tone: "forest", icon: UserCheck, sub: `Pipeline origin-side` },
    { label: "Territorios", value: t.territories, tone: "carbon", icon: Compass, sub: `Países + municipios` },
  ];

  return (
    <>
      <PageHeader
        label="Centro de operación"
        title="Dashboard"
        description="Visión consolidada de la comunidad: usuarios, pipelines, territorios y actividad IA."
      />
      <PageBody>
        {/* KPI bento */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {kpis.map((k) => (
            <Card key={k.label} className="p-5" testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-start justify-between mb-3">
                <span className="section-label">{k.label}</span>
                <k.icon size={16} className="text-carbon-400" />
              </div>
              <div className="text-3xl font-light tracking-tighter text-carbon-900 font-heading">{k.value}</div>
              <div className="text-xs text-carbon-500 mt-2">{k.sub}</div>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
          {/* Timeline */}
          <Card className="p-6 lg:col-span-2" testid="chart-timeline">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="section-label">Mensajería</div>
                <h2 className="font-heading text-xl">Últimos 14 días</h2>
              </div>
              <Badge tone="forest">Inbound vs Outbound</Badge>
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={stats.timeline}>
                  <CartesianGrid stroke="#E5DFD5" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#686D68" }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11, fill: "#686D68" }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5DFD5", fontSize: 12 }} />
                  <Line type="monotone" dataKey="inbound" stroke="#2C4C3B" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="outbound" stroke="#C55A44" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Role distribution */}
          <Card className="p-6" testid="chart-roles">
            <div className="section-label mb-1">Distribución</div>
            <h2 className="font-heading text-xl mb-4">Por rol</h2>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={stats.role_distribution} layout="vertical" margin={{ left: 24 }}>
                  <CartesianGrid stroke="#E5DFD5" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#686D68" }} />
                  <YAxis dataKey="role" type="category" tick={{ fontSize: 11, fill: "#686D68" }} width={80} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5DFD5", fontSize: 12 }} />
                  <Bar dataKey="count" fill="#C55A44" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Top territories */}
        <Card className="p-6" testid="top-territories">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="section-label">Territorios</div>
              <h2 className="font-heading text-xl">Top engagement</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.top_territories.map((t) => (
              <div key={`${t.country}-${t.municipality}`} className="border border-carbon-200 rounded p-4 hover:border-terracotta transition-colors">
                <div className="text-xs text-carbon-500">{t.country}</div>
                <div className="font-heading text-base">{t.municipality || "—"}</div>
                <div className="flex items-center gap-3 mt-2 text-xs text-carbon-500">
                  <span><b className="text-carbon-900">{t.founder_count}</b> founders</span>
                  <span><b className="text-carbon-900">{t.ambassador_count}</b> emb.</span>
                  <span className="ml-auto font-mono text-terracotta">{t.engagement_score?.toFixed?.(2) ?? "0.00"}</span>
                </div>
              </div>
            ))}
            {stats.top_territories.length === 0 && (
              <div className="text-sm text-carbon-500 col-span-full">Sin territorios aún. Carga el seed o crea usuarios.</div>
            )}
          </div>
        </Card>
      </PageBody>
    </>
  );
}
